import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { useActionToast } from '../../../services/actionToast';
import { PageTitle } from '../../../components/PageTitle';
import { DataSkeleton, EmptyState } from '../../../components/states';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/auth';
import AlertModal from '../../../components/AlertModal';

interface Institution {
  id: number;
  name: string;
  template_strategy?: 'DEFAULT' | 'MANUAL' | null;
}

interface Template {
  id: number;
  name: string;
  description?: string;
  type?: string;
  user_type?: string;
  school_class?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
  is_official: boolean;
  link?: string | null;
  file?: {
    original?: string | null;
  };
}

interface LinkedTemplate extends Template {
  link_id: number;
  custom_name?: string;
  custom_description?: string;
  is_active: boolean;
}

export function InstitutionTemplateLinkPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<number | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [linkedTemplates, setLinkedTemplates] = useState<LinkedTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<number>>(new Set());
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [standardInstitutionCount, setStandardInstitutionCount] = useState<number>(0);
  const institutionRequestVersion = useRef(0);
  const toast = useActionToast();
  const { user } = useAuth();
  const selectedTemplatesCount = selectedTemplates.size;
  const selectedInstitutionData = useMemo(
    () => institutions.find((inst) => inst.id === selectedInstitution) ?? null,
    [institutions, selectedInstitution]
  );
  const isDefaultTemplateStrategyForInstitution =
    selectedInstitutionData?.template_strategy === 'DEFAULT';

  const filteredAvailableTemplates = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableTemplates;
    }

    const term = searchTerm.toLowerCase();
    return availableTemplates.filter((template) => {
      const name = template.name?.toLowerCase() ?? '';
      const description = template.description?.toLowerCase() ?? '';
      const type = template.type?.toLowerCase() ?? '';
      const userType = template.user_type?.toLowerCase() ?? '';
      const categoryName = template.category?.name?.toLowerCase() ?? '';
      const className = template.school_class?.name?.toLowerCase() ?? '';

      return (
        name.includes(term) ||
        description.includes(term) ||
        type.includes(term) ||
        userType.includes(term) ||
        categoryName.includes(term) ||
        className.includes(term)
      );
    });
  }, [availableTemplates, searchTerm]);

  // Load institutions on mount
  useEffect(() => {
    loadInstitutions();
  }, []);

  useEffect(() => {
    institutionRequestVersion.current += 1;
    const requestVersion = institutionRequestVersion.current;
    setAvailableTemplates([]);
    setLinkedTemplates([]);
    setStandardInstitutionCount(0);
    setSelectedTemplates(new Set());
    setSearchTerm('');

    if (selectedInstitution) {
      loadAvailableTemplates(selectedInstitution, requestVersion);
      loadLinkedTemplates(selectedInstitution, requestVersion);
      loadStandardInstitutionCount(selectedInstitution, requestVersion);
    } else {
      setLoadingAvailable(false);
      setLoadingLinked(false);
    }
  }, [selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      setLoadingInstitutions(true);
      const response = await api.get('/v1/admin/institutions');
      setInstitutions(response.data.data || []);
    } catch (error) {
      toast({
        title: 'Erro ao carregar instituições',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingInstitutions(false);
    }
  };

  const loadAvailableTemplates = async (
    institutionId = selectedInstitution,
    requestVersion = institutionRequestVersion.current
  ) => {
    if (!institutionId) return;

    try {
      setLoadingAvailable(true);
      const response = await api.get(`/v1/admin/institutions/${institutionId}/templates/available`);
      if (institutionRequestVersion.current === requestVersion) {
        setAvailableTemplates(response.data.data || []);
      }
    } catch (error) {
      if (institutionRequestVersion.current === requestVersion) {
        setAvailableTemplates([]);
        toast({
          title: 'Erro ao carregar templates disponíveis',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      if (institutionRequestVersion.current === requestVersion) setLoadingAvailable(false);
    }
  };

  const loadLinkedTemplates = async (
    institutionId = selectedInstitution,
    requestVersion = institutionRequestVersion.current
  ) => {
    if (!institutionId) return;

    try {
      setLoadingLinked(true);
      const response = await api.get(`/v1/admin/institutions/${institutionId}/templates`);
      if (institutionRequestVersion.current === requestVersion) {
        setLinkedTemplates(response.data.data || []);
      }
    } catch (error) {
      if (institutionRequestVersion.current === requestVersion) {
        setLinkedTemplates([]);
        toast({
          title: 'Erro ao carregar templates vinculados',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      if (institutionRequestVersion.current === requestVersion) setLoadingLinked(false);
    }
  };

  const loadStandardInstitutionCount = async (
    institutionId = selectedInstitution,
    requestVersion = institutionRequestVersion.current
  ) => {
    try {
      const standardInst = institutions.find((inst) => inst.id !== institutionId);
      if (standardInst) {
        const response = await api.get(`/v1/admin/institutions/${standardInst.id}/templates`);
        if (institutionRequestVersion.current === requestVersion) {
          setStandardInstitutionCount(response.data.data?.length || 0);
        }
      }
    } catch (error) {
      if (institutionRequestVersion.current === requestVersion) setStandardInstitutionCount(0);
    }
  };

  const handleTemplateToggle = (templateId: number) => {
    const newSelected = new Set(selectedTemplates);
    if (newSelected.has(templateId)) {
      newSelected.delete(templateId);
    } else {
      newSelected.add(templateId);
    }
    setSelectedTemplates(newSelected);
  };

  const handleLinkTemplates = async () => {
    if (!selectedInstitution || selectedTemplates.size === 0) {
      toast({
        title: 'Selecione ao menos um template',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoadingAction(true);
      // Link each selected template
      const promises = Array.from(selectedTemplates).map((templateId) =>
        api.post(`/v1/admin/institutions/${selectedInstitution}/templates/${templateId}`)
      );

      await Promise.all(promises);

      toast({
        title: 'Templates vinculados com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reload data
      setSelectedTemplates(new Set());
      await loadAvailableTemplates();
      await loadLinkedTemplates();
    } catch (error) {
      toast({
        title: 'Erro ao vincular templates',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUnlinkTemplate = async (templateId: number) => {
    if (!selectedInstitution) return;

    const templateToRemove = linkedTemplates.find((item) => item.id === templateId);
    if (!templateToRemove) return;

    if (templateToRemove.is_official) {
      toast({
        title: 'Template padrão não pode ser removido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (linkedTemplates.length <= 1) {
      toast({
        title: 'Mantenha pelo menos um template vinculado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoadingAction(true);
      await api.delete(`/v1/admin/institutions/${selectedInstitution}/templates/${templateId}`);

      toast({
        title: 'Template desvinculado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reload data
      await loadAvailableTemplates();
      await loadLinkedTemplates();
    } catch (error) {
      toast({
        title: 'Erro ao desvincular template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleActive = async (linkId: number, currentStatus: boolean) => {
    try {
      setLoadingAction(true);
      await api.put(`/v1/admin/institutions/links/${linkId}`, { is_active: !currentStatus });

      toast({
        title: 'Status atualizado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await loadLinkedTemplates();
    } catch (error) {
      toast({
        title: 'Erro ao atualizar status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAction(false);
    }
  };

  // Check if user has permission (Sistema or Tutor groups)
  const canManageLinks = user?.group?.id === 1 || user?.group?.id === 3;

  if (!canManageLinks) {
    return (
      <Box>
        <PageTitle>Vincular Templates a Instituições</PageTitle>
        <Text>Você não tem permissão para acessar esta página.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle>Vincular Templates a Instituições</PageTitle>

      <Alert status="info" mb={6} borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="600" mb={1}>
            Como funcionam os Templates?
          </Text>
          <Text fontSize="sm">
            Templates são estruturas de dados modulares que contêm: Módulos (agrupamentos
            pedagógicos), Categorias (classificações de materiais), Materiais Extras e Tópicos
            (assuntos específicos). Ao vincular um template a uma instituição, você pode reutilizar
            toda essa hierarquia sem precisar recriar cada elemento. Por exemplo: você pode querer
            usar a mesma estrutura de Tópicos e Categorias, mas com Materiais Extras personalizados
            para aquela instituição específica.
          </Text>
        </Box>
      </Alert>

      <VStack spacing={6} align="stretch">
        {/* Institution Selection */}
        <Box bg="white" p={6} borderRadius="md" shadow="sm">
          <Box mb={4}>
            <Heading size="md">Selecione a Instituição</Heading>
          </Box>
          <Box>
            <FormControl>
              <FormLabel>Instituição</FormLabel>
              <Select
                placeholder="Selecione uma instituição"
                value={selectedInstitution || ''}
                onChange={(e) => setSelectedInstitution(Number(e.target.value))}
                isDisabled={loadingInstitutions}
              >
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Show templates only when institution is selected */}
        {selectedInstitution && (
          <>
            {/* Available Templates */}
            <Box bg="white" p={6} borderRadius="md" shadow="sm">
              {isDefaultTemplateStrategyForInstitution && (
                <Alert status="info" borderRadius="md" mb={4}>
                  <AlertIcon />
                  Esta instituição foi criada com o template padrão. Revise os templates oficiais
                  abaixo e vincule apenas os conteúdos que deseja disponibilizar.
                </Alert>
              )}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                gap={4}
                mb={4}
              >
                <Box>
                  <Heading size="md">
                    Templates Disponíveis
                    {filteredAvailableTemplates.length > 0 && (
                      <Text as="span" fontSize="sm" color="gray.500">
                        {' '}
                        ({filteredAvailableTemplates.length})
                      </Text>
                    )}
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Selecione os templates oficiais para replicar conteúdos, materiais extras e
                    tópicos automaticamente nesta instituição.
                  </Text>
                  {linkedTemplates.length > 0 && standardInstitutionCount > 0 && (
                    <Box
                      mt={2}
                      p={3}
                      bg="blue.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="blue.200"
                    >
                      <Text fontSize="sm" color="blue.800" fontWeight="600">
                        Comparação de materiais extras:
                      </Text>
                      <Text fontSize="sm" color="blue.700" mt={1}>
                        Instituições com template padrão:{' '}
                        <strong>{standardInstitutionCount} materiais extras</strong>
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Esta instituição: <strong>{linkedTemplates.length} materiais extras</strong>
                      </Text>
                      {linkedTemplates.length < standardInstitutionCount && (
                        <Text fontSize="xs" color="blue.600" mt={2} fontStyle="italic">
                          Vincule mais {standardInstitutionCount - linkedTemplates.length} templates
                          para igualar às instituições padrão
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  align={{ base: 'stretch', md: 'center' }}
                  gap={3}
                  w={{ base: '100%', md: 'auto' }}
                >
                  <Input
                    placeholder="Buscar por nome, categoria ou público"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                    maxW={{ base: '100%', md: '280px' }}
                    isDisabled={loadingAvailable}
                  />
                  <Button
                    colorScheme="blue"
                    onClick={handleLinkTemplates}
                    isLoading={loadingAction}
                    isDisabled={selectedTemplatesCount === 0}
                  >
                    {selectedTemplatesCount > 0
                      ? `Vincular ${selectedTemplatesCount} template${
                          selectedTemplatesCount > 1 ? 's' : ''
                        }`
                      : 'Vincular templates'}
                  </Button>
                </Flex>
              </Flex>
              <Box>
                {loadingAvailable && (
                  <DataSkeleton variant="cards" rows={3} label="Carregando modelos disponíveis" />
                )}
                {!loadingAvailable && filteredAvailableTemplates.length === 0 && (
                  <EmptyState
                    title={
                      searchTerm ? 'Nenhum modelo para esta busca' : 'Nenhum modelo disponível'
                    }
                    description={
                      searchTerm
                        ? 'Limpe a busca para consultar todos os modelos disponíveis.'
                        : 'Todos os modelos já estão vinculados ou não existem modelos cadastrados.'
                    }
                    action={
                      searchTerm
                        ? { label: 'Limpar busca', onClick: () => setSearchTerm('') }
                        : undefined
                    }
                  />
                )}
                {!loadingAvailable && filteredAvailableTemplates.length > 0 && (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {filteredAvailableTemplates.map((template) => (
                      <Box key={template.id} borderWidth="1px" borderRadius="md" p={4}>
                        <Box>
                          <Checkbox
                            isChecked={selectedTemplates.has(template.id)}
                            onChange={() => handleTemplateToggle(template.id)}
                            mb={2}
                            isDisabled={loadingAction}
                          >
                            <Text fontWeight="bold">{template.name}</Text>
                          </Checkbox>
                          {template.description && (
                            <Text fontSize="sm" color="gray.600" mb={2}>
                              {template.description}
                            </Text>
                          )}
                          <Flex gap={2} flexWrap="wrap">
                            {template.type && <Badge colorScheme="purple">{template.type}</Badge>}
                            {template.school_class && (
                              <Badge colorScheme="blue">{template.school_class.name}</Badge>
                            )}
                            {template.category && (
                              <Badge colorScheme="teal">{template.category.name}</Badge>
                            )}
                            {template.user_type && (
                              <Badge colorScheme="orange">{template.user_type}</Badge>
                            )}
                            {template.is_official && <Badge colorScheme="green">Oficial</Badge>}
                          </Flex>
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </Box>
            </Box>

            {/* Linked Templates */}
            <Box bg="white" p={6} borderRadius="md" shadow="sm">
              <Box mb={4}>
                <Heading size="md">Templates Vinculados</Heading>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Cada instituição precisa manter ao menos um template vinculado. Utilize a
                  vinculação padrão como base e adicione conjuntos personalizados quando necessário.
                </Text>
              </Box>
              <Box>
                {loadingLinked && (
                  <DataSkeleton variant="detail" rows={4} label="Carregando modelos vinculados" />
                )}
                {!loadingLinked && linkedTemplates.length === 0 && (
                  <EmptyState
                    title="Nenhum modelo vinculado"
                    description="Vincule um modelo para disponibilizar materiais nesta instituição."
                  />
                )}
                {!loadingLinked && linkedTemplates.length > 0 && (
                  <Stack spacing={3}>
                    {linkedTemplates.map((template) => {
                      const effectiveDescription =
                        template.custom_description || template.description || '';
                      const resourceLink = template.link || template.file?.original || null;
                      const statusLabel = template.is_active ? 'Ativo' : 'Inativo';
                      const templateLabel = template.is_official
                        ? 'Template padrão'
                        : 'Template personalizado';
                      const templateInfo = template.is_official
                        ? 'Conjunto essencial para o funcionamento do aplicativo.'
                        : 'Conjunto adicional que complementa o padrão.';
                      const disableRemoval = template.is_official || linkedTemplates.length <= 1;

                      return (
                        <Box key={template.link_id} borderWidth="1px" borderRadius="md" p={4}>
                          <Flex justify="space-between" align="start" gap={4} flexWrap="wrap">
                            <Box flex={1} minW="240px">
                              <Text fontWeight="bold">{template.custom_name || template.name}</Text>
                              {effectiveDescription && (
                                <Text fontSize="sm" color="gray.600" mt={1}>
                                  {effectiveDescription}
                                </Text>
                              )}
                              <Flex gap={2} mt={3} flexWrap="wrap" align="center">
                                <Badge colorScheme={template.is_official ? 'purple' : 'blue'}>
                                  {templateLabel}
                                </Badge>
                                <Badge colorScheme={template.is_active ? 'green' : 'red'}>
                                  {statusLabel}
                                </Badge>
                              </Flex>
                              <Text fontSize="sm" color="gray.600" mt={2}>
                                {templateInfo}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                Abrange conteúdos, materiais extras, tópicos, categorias e módulos
                                definidos pelo template selecionado.
                              </Text>
                              <Flex gap={2} mt={2} flexWrap="wrap">
                                {template.type && (
                                  <Badge colorScheme="purple">{template.type}</Badge>
                                )}
                                {template.school_class && (
                                  <Badge colorScheme="blue">{template.school_class.name}</Badge>
                                )}
                                {template.category && (
                                  <Badge colorScheme="teal">{template.category.name}</Badge>
                                )}
                                {template.user_type && (
                                  <Badge colorScheme="orange">{template.user_type}</Badge>
                                )}
                                <Badge colorScheme={template.is_active ? 'green' : 'red'}>
                                  {statusLabel}
                                </Badge>
                              </Flex>
                              {resourceLink && (
                                <Button
                                  size="sm"
                                  mt={2}
                                  variant="outline"
                                  onClick={() => window.open(resourceLink, '_blank')}
                                >
                                  Abrir material
                                </Button>
                              )}
                            </Box>
                            <Flex gap={2} flexWrap="wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme={template.is_active ? 'orange' : 'green'}
                                onClick={() =>
                                  handleToggleActive(template.link_id, template.is_active)
                                }
                                isLoading={loadingAction}
                              >
                                {template.is_active ? 'Pausar vínculo' : 'Ativar vínculo'}
                              </Button>
                              <AlertModal
                                alertMsg={
                                  <Text fontWeight="bold" textAlign="center" fontSize="xl">
                                    Confirma a remoção deste vínculo?
                                  </Text>
                                }
                                msgStatus="warning"
                                ButtonOpen={({ onClick }: any) => (
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    onClick={onClick}
                                    isLoading={loadingAction}
                                    isDisabled={loadingAction || disableRemoval}
                                    title={
                                      disableRemoval
                                        ? 'Mantenha pelo menos um template vinculado'
                                        : undefined
                                    }
                                  >
                                    Remover vínculo
                                  </Button>
                                )}
                                ConfirmButton={
                                  <Button
                                    colorScheme="red"
                                    onClick={() => handleUnlinkTemplate(template.id)}
                                    isLoading={loadingAction}
                                  >
                                    Confirmar remoção
                                  </Button>
                                }
                              />
                            </Flex>
                          </Flex>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
}
